import React from 'react';
import PropTypes from 'prop-types';
import Comment from '../input/Comment.component';
import {PopUp} from './PopUp.component';
import {DOCUMENT_ACTIONS} from 'utils/constants';
import translate from '../translations/translations.wrapper.jsx';
import * as Api from 'utils/api/api.js';
import {handleError} from 'utils/errorHandle.function';
import {PATH_TO_DOCUMENT_OVERVIEW} from "components/router/paths.js";
import WorkflowDetailsWizard
    from "components/taskdetails-remake/panels/workflowdetails/WorkflowDetailsWizard.component.jsx";
import {SimulatorErrorsLog} from "components/workflowdetails/simulator/Simulator.component.jsx";
import DisplayProgress from "components/common/displayProgress";
import { Notification, NotificationType } from "components/common/Notification.component";
import { Button, ButtonType } from "components/common/Button.component";

//this is to make sure the tabbing stays inside the dialog
const TAB_INDEX = 1000;

/**
 * popup which will be used in case user has selected 'approve with comment'
 * expects as props:
 * list of selected tasks
 * close popup callback
 * translate function
 */
export class SendForApproval extends PopUp {

    static propTypes = {
        tasks: PropTypes.array, // selected tasks
        closeCallback: PropTypes.func, //callback for closing the popup
        handledTaskCallback: PropTypes.func, //callback to mark task as handled
        translate: PropTypes.func, // to translate texts
        showNotification: PropTypes.func, // to show transient notification
        navigate: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.ok = this.ok.bind(this);
        this.buildContent = this.buildContent.bind(this);
        this.focusFirst = this.focusFirst.bind(this);

        this.state = {
            comment: '',
            actionRunning: this.props.tasks.length === 1, // the proofing starts running at componentdidmount
            workflow: "",
            workflowMap: "",
            warning: undefined,
            loadingSimulator: null
        }

        this.commentInput = React.createRef();
        this.okButton = React.createRef();
        this.cancelButton = React.createRef();
    }

    ok() {
        const self = this;

        this.setState({actionRunning: true});

        const taskIds = this.props.tasks.map((task) => {
            return task.key;
        });

        let commentToSend = this.state.comment ? this.state.comment : '';

        Api.sendDocumentForApproval(commentToSend, taskIds, this.props.companyId).then(response => {
            self.props.showNotification(response.message);
            self.props.handledTaskCallback(taskIds, self.props.navigate);
            self.props.closeCallback();
            self.props.navigate(PATH_TO_DOCUMENT_OVERVIEW);
        }, error => {
            handleError(error);
        });

    }

    componentDidMount() {
        super.componentDidMount();
        this.focusFirst();

        //we can run the simulator for just one task
        if (this.props.tasks.length === 1) {
            this.setState({
                loadingSimulator: true
            });

            Api._simulateWorkflowGraph(this.props.tasks[0].key, this.props.companyId).then(response => {
                    this.setState({
                        workflowGraph: response,
                        loadingSimulator: false,
                        actionRunning: false
                    });
                },
                error => {
                    this.setState({
                        warning: error.errorMessages
                    });

                    this.cancelButton?.current?.focus();
                });

        }
    }

    focusFirst() {
        this.commentInput?.current?.focus();
    }

    buildContent() {

        const okEnabled = !this.state.actionRunning;

        return (
            <div>
                <div className="modal-body mb-4">
                    <Comment
                        ref={this.commentInput}
                        inputHint={this.props.translate("popUp.approve.commentHint")}
                        translate={this.props.translate}
                        tabIndex={TAB_INDEX}
                        id="comment"
                        value={this.state.comment}
                        propagateValue={value => this.setState({comment: value})}
                        label={this.props.translate("popUp.approve.commentLabel")}
                    />
                    {this.props.tasks.length === 1 &&
                        <div>
                            {this.state.workflowGraph ?
                                <div>
                                    <WorkflowDetailsWizard data={this.state.workflowGraph.steps}
                                                           hideSkippedSteps={true}
                                    />
                                    {this.state.workflowGraph.success === false && this.state.workflowGraph?.log &&
                                        <SimulatorErrorsLog nodes={this.state.workflowGraph.log.children}
                                                            bsClass={"overflow-auto send-for-approval-log mb-2"}
                                                            title={this.props.translate("workflowDetails.simulator.errorLog")}/>

                                    }
                                    <div className="my-3">
                                        <Notification type={this.state.workflowGraph.success ? NotificationType.SUCCESS : NotificationType.ERROR } description={
                                            this.state.workflowGraph.success ?
                                                `${this.props.translate("popUp.sendforapproval.successText")}`:
                                                `${this.props.translate("popUp.sendforapproval.errorText")}`
                                        }/>
                                    </div>
                                </div> :

                                <div>
                                    <div className="simulator col-md-12 mt-2 px-0 panel panel-default">
                                        {this.state.loadingSimulator &&
                                            <DisplayProgress message={this.props.translate("popUp.sendforapproval.runningSimulation")}/>
                                        }
                                    </div>
                                </div>
                            }

                        </div>
                    }
                </div>

                <div className="modal-footer">
                    <div className="d-flex gap-8 float-right">
                        <Button type={ButtonType.PRIMARY}
                                onClick={this.ok}
                                disabled={!okEnabled || this.state.warning}
                                ref={this.okButton}
                                tabIndex={TAB_INDEX + 1}
                                text={this.props.translate("popUp.ok")}
                        />
                        <Button type={ButtonType.SECONDARY}
                                onBlur={this.focusFirst}
                                ref={this.cancelButton}
                                onClick={this.props.closeCallback}
                                tabIndex={TAB_INDEX + 2}
                                text={this.props.translate("popUp.cancel")}
                        />
                    </div>
                </div>
            </div>

        );
    }

    getType() {
        return DOCUMENT_ACTIONS.SEND_FOR_APPROVAL;
    }

    getTitle() {
        if (this.props.tasks.length === 1)
            return this.props.translate("popUp.sendforapproval.titleSingle", this.props.tasks[0].description);
        return this.props.translate("popUp.sendforapproval.titleMultiple", this.props.tasks.length);
    }
}

const withTranslations = translate(SendForApproval);
export default withTranslations;